<template>
    <div class="distance-calculator__content__form__search-select">
        <input ref="inputRef" :class="{ 'error': showError }" class="distance-calculator__content__form__search-select__input"
            type="text" v-model="searchTerm" @focus="handleFocus" @input="handleInput" />
        <label :class="{ 'distance-calculator__content__form__search-select__label--up': isFocused || searchTerm }"
            class="distance-calculator__content__form__search-select__label">
            {{ placeholder }}
        </label>
        <span class="distance-calculator__content__form__search-select__icon">
            <CalculatorIcon :class="'magnify-icon'" symbolId="magnify" />
        </span>
        <ul class="distance-calculator__content__form__search-select__dropdown" v-show="isOpen" ref="dropdownRef">
            <li class="distance-calculator__content__form__search-select__dropdown__item"
                v-for="(item, index) in filteredItems" :key="index" @click="selectOption(item.Code, item.Name)">
                {{ item.Name }}
            </li>
        </ul>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted, onUnmounted } from 'vue';
import { getListByInput } from '../../api/APIService';
import CalculatorIcon from '../CalculatorIcon/CalculatorIcon.vue';
import Item from '../../types/types';
import { removeAirportWord } from '../../utils/utils';

const { placeholder } = defineProps({
    placeholder: String,
});
const emit = defineEmits(['update:value', 'update:filteredItems']);

const searchTerm = ref('');
const isOpen = ref(false);
const apiCallCompleted = ref(false);
const filteredItems = ref<Item[]>([]);
const isFocused = ref<boolean>(false);
const inputRef = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);
 
const handleInput = () => {
    if (searchTerm.value.length >= 2) {
        isOpen.value = true;
    } else {
        isOpen.value = false;
        filteredItems.value = [];
        apiCallCompleted.value = false;
    }
};

const handleFocus = () => {
    isFocused.value = true;
    if (searchTerm.value.length >= 2) {
        isOpen.value = true;
    }
};

const selectOption = (code: string, name: string) => {
    searchTerm.value = name;
    isOpen.value = false;
    emit('update:value', { code, name });
};

const showError = computed(() => {
    return (
        apiCallCompleted.value &&
        searchTerm.value.length >= 2 &&
        filteredItems.value.length === 0 &&
        !isFocused.value
    );
});

watch(searchTerm, async (newValue) => {
    if (newValue.length >= 2) {
        try {
            let data = {
                url: 'filterlist',
                payload: {
                    filter: newValue,
                    list: 'airports'
                }
            };
            const response = await getListByInput(data);
            if (response?.status === 200) {
                apiCallCompleted.value = true;
                let responseData = response?.data?.filter((val: any) => val?.IsActive === true);
                responseData = responseData.map((val: any) => ({
                    ...val,
                    Name: removeAirportWord(val.Name)
                }));
                filteredItems.value = responseData;
                emit('update:filteredItems', responseData);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    } else {
        filteredItems.value = [];
        isOpen.value = false;
        apiCallCompleted.value = false;
    }
});


const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Node;
  const clickedOutside = !(
    dropdownRef.value?.contains(target) ||
    inputRef.value?.contains(target)
  );

  if (clickedOutside) {
    isOpen.value = false;
    isFocused.value = false;
  }
};


onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});

const clearSearchFunction = () => {
    searchTerm.value = '';
    filteredItems.value = [];
    isOpen.value = false;
    apiCallCompleted.value = false;
};

defineExpose({
    clearSearchFunction
});
</script>